import { useCallback } from 'react';
import { useGlobalStore } from '@siloreal:app:store';
import {
  i18n,
  isLanguage,
  setI18nLocale,
  translate,
  type Language,
  type TranslationOptions,
} from '@siloreal:shared:sdk/locale';
import { useGlobalStore as useOldGlobalStore } from '@old/global-store/use-store';
import legacyI18n from '../../old/libs/language';

const LEGACY_LANGUAGE_PREFERENCE_KEY = 'LANGUAGE_PREFERENCE';

export type LocaleState = {
  locale: Language;
  t: (scope: string, options?: TranslationOptions) => string;
  updateLanguage: (nextLocale: Language) => typeof i18n;
};

type LocaleListener = (state: LocaleState, previousState: LocaleState) => void;

function getCurrentLocale(): Language {
  return useGlobalStore.getState().user_locale;
}

function persistLegacyLanguagePreference(locale: Language): void {
  try {
    localStorage.setItem(LEGACY_LANGUAGE_PREFERENCE_KEY, locale);
  } catch {
    // private mode / quota
  }
}

function setLocale(nextLocale: Language): typeof i18n {
  setI18nLocale(nextLocale);
  legacyI18n.locale = nextLocale;
  persistLegacyLanguagePreference(nextLocale);

  const { user_locale: currentLocale, user_set_locale: setStoreLocale } = useGlobalStore.getState();
  if (currentLocale !== nextLocale) setStoreLocale(nextLocale);

  const { preferedLanguageId, setLanguage } = useOldGlobalStore.getState();
  if (preferedLanguageId !== nextLocale) setLanguage(nextLocale);

  return i18n;
}

function translateCurrent(scope: string, options?: TranslationOptions): string {
  return translate(scope, getCurrentLocale(), options);
}

function updateLanguage(nextLocale: Language): typeof i18n {
  return setLocale(nextLocale);
}

function createLocaleState(locale: Language): LocaleState {
  return {
    locale,
    t: translateCurrent,
    updateLanguage,
  };
}

function getLocaleState(): LocaleState {
  return createLocaleState(getCurrentLocale());
}

function subscribeLocale(listener: LocaleListener): () => void {
  return useGlobalStore.subscribe((state, previousState) => {
    if (state.user_locale === previousState.user_locale) return;

    listener(createLocaleState(state.user_locale), createLocaleState(previousState.user_locale));
  });
}

function syncLegacyLanguage(): void {
  const legacyLocale = useOldGlobalStore.getState().preferedLanguageId;

  if (isLanguage(legacyLocale)) {
    setLocale(legacyLocale);
    return;
  }

  const currentLocale = getCurrentLocale();
  setI18nLocale(currentLocale);
  legacyI18n.locale = currentLocale;
}

const unsubscribeLocaleSync = useGlobalStore.subscribe((state, previousState) => {
  if (state.user_locale !== previousState.user_locale) setI18nLocale(state.user_locale);
});

const unsubscribeLegacySync = useOldGlobalStore.subscribe((state, previousState) => {
  if (state.preferedLanguageId !== previousState.preferedLanguageId) syncLegacyLanguage();
});

const unsubscribeLocaleHydration = useGlobalStore.persist.onFinishHydration(syncLegacyLanguage);
const unsubscribeLegacyHydration = useOldGlobalStore.persist.onFinishHydration(syncLegacyLanguage);

syncLegacyLanguage();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribeLocaleSync();
    unsubscribeLegacySync();
    unsubscribeLocaleHydration();
    unsubscribeLegacyHydration();
  });
}

function useLocaleHook(): LocaleState {
  const locale = useGlobalStore((state) => state.user_locale);
  const t = useCallback(
    (scope: string, options?: TranslationOptions) => translate(scope, locale, options),
    [locale]
  );

  return {
    locale,
    t,
    updateLanguage,
  };
}

export const useLocale = Object.assign(useLocaleHook, {
  getState: getLocaleState,
  subscribe: subscribeLocale,
});
